import { MedplumClient } from "@medplum/core";
import { Condition } from "@medplum/fhirtypes";

import { withProblemListMembership } from "../utils/diagnosis-problem-list";
import { isStagedResourceId } from "../utils/staged-procedures";

type SetDiagnosisProblemListMembershipParams = {
  medplum: MedplumClient;
  /** The encounter's diagnosis, as the tab is showing it. */
  condition: Condition;
  /** Whether the client should end up with this diagnosis on their problem list. */
  isMember: boolean;
};

export type SetDiagnosisProblemListMembershipResult =
  | { ok: true; data: Condition }
  /**
   * `unsaved-diagnosis`: the diagnosis is only staged, so there is no resource to write to.
   * `write-failed`: the update was rejected.
   */
  | { ok: false; reason: "unsaved-diagnosis" | "write-failed"; details?: unknown };

/**
 * Puts one of the encounter's diagnoses on the client's problem list, or takes it off.
 *
 * The membership lives on the diagnosis itself, so this is a change to the client's record made
 * through the encounter's resource — which is why it is written straight away rather than staged onto
 * the encounter's save the way the tab's other actions are. The encounter's own reconciliation carries
 * the membership across later saves and drops it when the diagnosis is retired.
 *
 * The stored resource is re-read before it is rewritten, rather than the copy the tab is rendering:
 * that copy can be a preview built from staged work, and writing a preview back would persist a
 * resource the server never held.
 */
export const setDiagnosisProblemListMembership = async ({
  medplum,
  condition,
  isMember,
}: SetDiagnosisProblemListMembershipParams): Promise<SetDiagnosisProblemListMembershipResult> => {
  if (!condition.id || isStagedResourceId(condition.id)) {
    return { ok: false, reason: "unsaved-diagnosis" };
  }

  try {
    const existing = await medplum.readResource("Condition", condition.id);
    const data = await medplum.updateResource(withProblemListMembership(existing, isMember));

    return { ok: true, data };
  } catch (error) {
    return { ok: false, reason: "write-failed", details: error };
  }
};
